import { FC } from 'react';

import { useFloating } from '@floating-ui/react';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Button, Dialog } from 'ui-kit';

import { SPECIAL_PII_NOTICE_TITLE, SpecialPiiNoticeState, useSpecialPiiNotice } from './piiNotice';

export const PII_NOTICE_LOADING_COPY = `Loading the ${SPECIAL_PII_NOTICE_TITLE}…`;
export const PII_NOTICE_FAILED_COPY = `Failed to load the ${SPECIAL_PII_NOTICE_TITLE}.`;
export const PII_NOTICE_REFUSED_COPY =
  "You don't have Level 2 access to an open sale now, so Level 2 files can't be opened.";

const NoticeBody: FC<{ notice: SpecialPiiNoticeState }> = ({ notice }) => {
  if (notice.status === 'ready') {
    return <p className="mt-4 text-left text-sm leading-6 text-gray-200">{notice.text}</p>;
  }
  if (notice.status === 'loading') {
    return (
      <p role="status" className="mt-4 text-sm leading-6 text-gray-300">
        {PII_NOTICE_LOADING_COPY}
      </p>
    );
  }
  return (
    <p role="alert" className="mt-4 text-sm leading-6 text-status-error">
      {notice.status === 'refused' ? PII_NOTICE_REFUSED_COPY : PII_NOTICE_FAILED_COPY}
    </p>
  );
};

// Accepting needs the notice api served: until it loads there is nothing to accept, and the
// version passed on is the one shown. A refusal closes through onRefusalClosed where the caller
// would otherwise offer the notice again, which api would refuse again.
export const PiiNoticeModal: FC<{
  onAccept: (version: string) => void;
  onDecline: () => void;
  onRefusalClosed?: () => void;
}> = ({ onAccept, onDecline, onRefusalClosed }) => {
  const notice = useSpecialPiiNotice();
  const close = (notice.status === 'refused' && onRefusalClosed) || onDecline;
  const setIsOpen = (open: boolean) => {
    if (!open) {
      close();
    }
  };
  const { refs, context } = useFloating({ open: true, onOpenChange: setIsOpen });

  return (
    <Dialog dialog={{ isOpen: true, setIsOpen, refs, context }}>
      <div className="max-w-lg">
        <div
          className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-700 text-yellow-300"
          aria-hidden="true"
        >
          <IconAlertTriangle size={26} />
        </div>
        <h2 className="mt-4 font-heebo text-xl font-semibold text-white">
          {SPECIAL_PII_NOTICE_TITLE}
        </h2>
        <NoticeBody notice={notice} />
        <div className="mt-6 flex justify-center gap-3">
          <Button label="Cancel" type="secondary" onClick={close}>
            Cancel
          </Button>
          {notice.status === 'ready' && (
            <Button label="I understand and continue" onClick={() => onAccept(notice.version)}>
              I understand and continue
            </Button>
          )}
          {notice.status === 'failed' && (
            <Button label="Retry" onClick={notice.retry}>
              Retry
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};
